/* eslint-disable max-lines */
/**
 * CentralFillOrdersActionModal (POM-5455)
 *
 * Neutral two-step (details → confirm) bulk-action modal for the Compounding
 * queue. All action-specific copy, colours, the API call, and the snackbar
 * messages are supplied by the caller, so Recall and Force Return are thin
 * wrappers around this shared shell rather than two copies of the same layout.
 *
 * A note/reason is always required: the primary action stays disabled until a
 * non-empty value is entered.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';

const COLS = '110px 140px 1fr 1fr 120px';
const roSx = { fontSize: '0.72rem' };
const roMonoSx = { fontFamily: 'monospace', fontSize: '0.72rem' };

const CentralFillOrdersActionModal = (props) => {
  const {
    open,
    orders,
    onClose,
    onProcessed,
    idPrefix,
    title,
    subtitle,
    warningText,
    noteLabel,
    noteHelperText,
    notePlaceholder,
    noteAriaLabel,
    primaryColor,
    getPrimaryLabel,
    confirmTitle,
    getConfirmCountText,
    consequences,
    notePreviewLabel,
    action,
    getSuccessMessage,
    getPartialMessage,
    getErrorMessage,
  } = props;

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const count = orders.length;
  const trimmedNote = note.trim();
  const isNoteValid = trimmedNote.length > 0;

  const handleClose = (event, reason) => {
    if (loading || reason === 'backdropClick') return;
    setNote('');
    onClose(event, reason);
  };

  const handleSubmit = () => {
    if (!isNoteValid) return;
    setLoading(true);

    const trackingIds = orders.map((order) => order.trackingId);

    action(trackingIds, trimmedNote)
      .then(({ successCount, failureCount }) => {
        if (failureCount === 0) {
          enqueueSnackbar(getSuccessMessage(successCount), {
            variant: 'success',
            autoHideDuration: 4000,
          });
        } else if (successCount === 0) {
          enqueueSnackbar(getErrorMessage(), { variant: 'error', autoHideDuration: 4000 });
        } else {
          enqueueSnackbar(getPartialMessage(successCount, failureCount), {
            variant: 'warning',
            persist: true,
          });
        }
        onProcessed();
      })
      .catch(() => {
        enqueueSnackbar(getErrorMessage(), { variant: 'error', autoHideDuration: 4000 });
        onProcessed();
      })
      .finally(() => {
        setLoading(false);
        setNote('');
        setConfirmOpen(false);
        onClose();
      });
  };

  // Destructive bulk action — the primary button opens a confirmation step
  // rather than firing directly.
  const handlePrimaryClick = () => {
    if (!isNoteValid) return;
    setConfirmOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        aria-labelledby={`${idPrefix}-dialog-title`}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 800,
            borderRadius: '10px',
            boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          },
        }}
      >
        <DialogTitle
          id={`${idPrefix}-dialog-title`}
          sx={{ pt: 3, pb: 2, px: 3, bgcolor: 'background.paper' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <ErrorOutlineIcon
                sx={{ color: 'error.main', fontSize: '1.6rem', mt: 0.15, flexShrink: 0 }}
              />
              <Box>
                <Typography
                  variant='h6'
                  fontWeight={700}
                  lineHeight={1.3}
                  color='text.primary'
                  sx={{ fontSize: '1.05rem' }}
                >
                  {title}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mt: 0.3, lineHeight: 1.6, fontSize: '0.78rem' }}
                >
                  {subtitle}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0, bgcolor: '#f7f8fa' }}>
          <Box sx={{ px: 2.5, pt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderLeft: '4px solid #f59e0b',
                borderRadius: '0 10px 10px 0',
                px: 2,
                py: 1.25,
                background: 'linear-gradient(135deg, #fffbeb 0%, #fefce8 100%)',
                border: '1px solid rgba(245,158,11,0.2)',
                borderLeftWidth: '4px',
                borderLeftColor: '#f59e0b',
              }}
            >
              <WarningAmberIcon sx={{ color: '#d97706', fontSize: '1.1rem', flexShrink: 0 }} />
              <Typography
                sx={{ fontSize: '0.78rem', lineHeight: 1.5, color: '#92400e', fontWeight: 500 }}
              >
                {warningText}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mx: 2.5,
              mt: 1.75,
              mb: 1.75,
              border: '1px solid',
              borderColor: 'rgba(0,0,0,0.09)',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                gap: 1.5,
                px: 2.5,
                py: 1.125,
                bgcolor: '#f8f9fb',
                borderBottom: '1px solid rgba(0,0,0,0.07)',
              }}
            >
              {['Order ID', 'Patient', 'Drug', 'Partner', 'Pharmacy'].map((col) => (
                <Typography
                  key={col}
                  variant='caption'
                  fontWeight={600}
                  sx={{
                    fontSize: '0.72rem',
                    letterSpacing: '0.02em',
                    color: '#94a3b8',
                  }}
                >
                  {col}
                </Typography>
              ))}
            </Box>

            <Box
              sx={{
                minHeight: 80,
                maxHeight: 300,
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 4 },
                '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#94a3b8' },
              }}
            >
              {orders.map((order, idx) => (
                <Box
                  key={order.trackingId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: COLS,
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.1,
                    borderBottom: idx < orders.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                    bgcolor: idx % 2 === 0 ? 'background.paper' : '#fafbfc',
                    transition: 'background-color 0.1s ease',
                    '&:hover': { bgcolor: '#f0f4ff' },
                  }}
                >
                  <Typography
                    noWrap
                    title={order.orderId || '—'}
                    sx={{ ...roMonoSx, color: '#1e293b', fontWeight: 700, lineHeight: 1.4 }}
                  >
                    {order.orderId || '—'}
                  </Typography>

                  <Typography
                    noWrap
                    title={order.patientName || 'Unknown Patient'}
                    sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#475569' }}
                  >
                    {order.patientName || 'Unknown Patient'}
                  </Typography>
                  <Typography
                    noWrap
                    title={order.drugName || '—'}
                    sx={{ ...roSx, color: '#64748b', fontStyle: 'italic', fontWeight: 400 }}
                  >
                    {order.drugName || '—'}
                  </Typography>
                  <Typography
                    noWrap
                    title={order.partnerName || '—'}
                    sx={{ ...roSx, color: '#1e293b', fontWeight: 500 }}
                  >
                    {order.partnerName || '—'}
                  </Typography>

                  <Typography
                    noWrap
                    title={order.compoundingPharmacyName || '—'}
                    sx={{ ...roSx, color: '#64748b' }}
                  >
                    {order.compoundingPharmacyName || '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              mx: 2.5,
              mb: 2.5,
              border: '1px solid',
              borderColor: 'rgba(0,0,0,0.09)',
              borderRadius: '12px',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
            }}
          >
            <Box sx={{ px: 2.5, pt: 1.75, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.25 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  sx={{ color: '#1e293b', fontSize: '0.82rem' }}
                >
                  {noteLabel}
                </Typography>
                <Typography
                  component='span'
                  sx={{ color: 'error.main', fontWeight: 700, fontSize: '0.82rem', lineHeight: 1 }}
                >
                  *
                </Typography>
              </Box>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 1.25, lineHeight: 1.6, fontSize: '0.75rem' }}
              >
                {noteHelperText}
              </Typography>
              <TextField
                placeholder={notePlaceholder}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                multiline
                minRows={1}
                maxRows={3}
                fullWidth
                size='small'
                disabled={loading}
                error={false}
                inputProps={{ 'aria-label': noteAriaLabel }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#f8f9fb',
                    fontSize: '0.82rem',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.12)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.25)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '1.5px',
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontStyle: 'italic',
                    color: '#94a3b8',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: 'rgba(0,0,0,0.07)' }} />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'background.paper',
          }}
        >
          <Chip
            label={`${count} ${count === 1 ? 'Item' : 'Items'}`}
            size='small'
            sx={{
              bgcolor: '#1e293b',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.72rem',
              height: 26,
              borderRadius: '999px',
              '& .MuiChip-label': { px: 1.5 },
            }}
          />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant='outlined'
              onClick={handleClose}
              disabled={loading}
              sx={{
                px: 2.5,
                fontSize: '0.82rem',
                fontWeight: 500,
                borderColor: 'rgba(0,0,0,0.15)',
                color: 'text.secondary',
                textTransform: 'none',
                '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'grey.50' },
              }}
            >
              Cancel
            </Button>
            {/* Outer (first-step) button never spins — only the Confirm button
                shows the loader. It just stays disabled while submitting. */}
            <LoadingButton
              onClick={handlePrimaryClick}
              variant='contained'
              color={primaryColor}
              disabled={loading || !isNoteValid}
              sx={{
                px: 3,
                fontWeight: 600,
                fontSize: '0.82rem',
                textTransform: 'none',
                letterSpacing: 0.2,
              }}
            >
              {getPrimaryLabel(count)}
            </LoadingButton>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={(event, reason) => {
          if (loading || reason === 'backdropClick') return;
          setConfirmOpen(false);
        }}
        maxWidth='xs'
        fullWidth
        aria-labelledby={`${idPrefix}-confirm-title`}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0,0,0,0.22)',
          },
        }}
      >
        {/* Header strip — brand navy (perfectRxBlue) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2.5,
            py: 1.75,
            bgcolor: 'primary.main',
            color: '#fff',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: '1.4rem' }} />
          <Typography
            id={`${idPrefix}-confirm-title`}
            variant='h6'
            fontWeight={700}
            sx={{ fontSize: '1rem' }}
          >
            {confirmTitle}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 2.5, py: 2.5 }}>
          {/* Count recap */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                minWidth: 48,
                height: 48,
                px: 1,
                borderRadius: '10px',
                bgcolor: 'rgba(3,28,87,0.08)',
                border: '1px solid rgba(3,28,87,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{ fontSize: '1.5rem', fontWeight: 800, color: 'primary.main', lineHeight: 1 }}
              >
                {count}
              </Typography>
            </Box>
            <Typography variant='body1' fontWeight={600} sx={{ color: '#1e293b', lineHeight: 1.4 }}>
              {getConfirmCountText(count)}
            </Typography>
          </Box>

          {/* Consequences */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: trimmedNote ? 2 : 0 }}
          >
            {consequences.map((line) => (
              <Box key={line} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    mt: '7px',
                    flexShrink: 0,
                  }}
                />
                <Typography variant='body2' sx={{ color: '#475569', lineHeight: 1.5 }}>
                  {line}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Note preview */}
          {trimmedNote && (
            <Box
              sx={{
                borderLeft: '3px solid',
                borderColor: 'rgba(0,0,0,0.12)',
                bgcolor: '#f8f9fb',
                borderRadius: '0 8px 8px 0',
                px: 1.5,
                py: 1,
              }}
            >
              <Typography
                variant='caption'
                sx={{
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  color: '#94a3b8',
                  mb: 0.25,
                }}
              >
                {notePreviewLabel}
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: '#334155', fontStyle: 'italic', wordBreak: 'break-word' }}
              >
                “{trimmedNote}”
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
          <Button
            variant='outlined'
            onClick={() => setConfirmOpen(false)}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderColor: 'rgba(0,0,0,0.15)',
              color: 'text.secondary',
              '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'grey.50' },
            }}
          >
            Cancel
          </Button>
          {/* Confirm shows only the default spinner while submitting (no text). */}
          <LoadingButton
            variant='contained'
            color={primaryColor}
            onClick={handleSubmit}
            loading={loading}
            sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

CentralFillOrdersActionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      trackingId: PropTypes.string.isRequired,
      orderId: PropTypes.string,
      patientName: PropTypes.string,
      drugName: PropTypes.string,
      partnerName: PropTypes.string,
      compoundingPharmacyName: PropTypes.string,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onProcessed: PropTypes.func.isRequired,
  idPrefix: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  warningText: PropTypes.string.isRequired,
  noteLabel: PropTypes.string.isRequired,
  noteHelperText: PropTypes.string.isRequired,
  notePlaceholder: PropTypes.string.isRequired,
  noteAriaLabel: PropTypes.string.isRequired,
  primaryColor: PropTypes.string.isRequired,
  getPrimaryLabel: PropTypes.func.isRequired,
  confirmTitle: PropTypes.string.isRequired,
  getConfirmCountText: PropTypes.func.isRequired,
  consequences: PropTypes.arrayOf(PropTypes.string).isRequired,
  notePreviewLabel: PropTypes.string.isRequired,
  action: PropTypes.func.isRequired,
  getSuccessMessage: PropTypes.func.isRequired,
  getPartialMessage: PropTypes.func.isRequired,
  getErrorMessage: PropTypes.func.isRequired,
};

export default CentralFillOrdersActionModal;
